import { useMemo, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useTable, usePagination, useGlobalFilter, useAsyncDebounce, useSortBy } from 'react-table'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import Icon from '@mui/material/Icon'
import Autocomplete from '@mui/material/Autocomplete'
import BulkFaqEdit from '../../Modals/BulkFaqEdit'
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'
import MDInput from 'components/MDInput'
import MDPagination from 'components/MDPagination'
import DataTableHeadCell from 'examples/Tables/DataTable/DataTableHeadCell'
import DataTableBodyCell from 'examples/Tables/DataTable/DataTableBodyCell'
import Multiselect from '../../MultiSelect'

function DataTable({
  entriesPerPage,
  canSearch,
  typeOptions,
  canFilter,
  showTotalEntries,
  table,
  pagination,
  isSorted,
  noEndBorder,
  unlimitedFaq,
  handleRefresh,
  categories,
  subcategories,
}) {
  const defaultValue = entriesPerPage ? entriesPerPage : 100
  const entries = entriesPerPage.entries
    ? entriesPerPage.entries.map((el) => el.toString())
    : ['10', '20', '30', '50', '100', '150', '200', '300', '400']
  const columns = useMemo(() => table.columns, [table])
  const data = useMemo(() => table.rows, [table])

  const [selectedCategories, setSelectedCategories] = useState([])
  const [selectedSubcategories, setSelectedSubcategories] = useState([])

   const [selectedFaqs, setSelectedFaqs] = useState([])
  const handleCheckboxChange = (event, row) => {
    if (event.target.checked) {
      setSelectedFaqs((prev) => [...prev, row.original])
    } else {
      setSelectedFaqs((prev) => prev.filter((faq) => faq !== row.original))
    }
  }

  const handleCategoryChange = (selectedOptions) => {
    setSelectedCategories(selectedOptions ? selectedOptions.map((option) => option.value) : [])
  }

  const handleSubcategoryChange = (selectedOptions) => {
    setSelectedSubcategories(selectedOptions ? selectedOptions.map((option) => option.value) : [])
  }

  // Filter data based on selected categories and subcategories
  const filteredData = useMemo(() => {
    if (!unlimitedFaq || (selectedCategories.length === 0 && selectedSubcategories.length === 0)) {
      return data
    }
    console.log('Selected categories:', selectedCategories)
    console.log('Selected subcategories:', selectedSubcategories)

    return data.filter((row) => {
      console.log('Row: ', row)
      if (!row) return false

      // Handle category matching
      const rowCategories = Array.isArray(row.category)
        ? row.category.map((cat) => cat?._id || cat?.value)
        : [row.category?._id || row.category?.value].filter(Boolean)
      console.log('Row categories:', rowCategories)

      const categoryMatch =
        selectedCategories.length === 0 ||
        rowCategories.some((catId) => selectedCategories.includes(catId))
      console.log('categoryMatch: ', categoryMatch)
      // Handle subcategory matching
      const rowSubcategories = Array.isArray(row.subcategory)
        ? row.subcategory.map((sub) => sub?._id || sub?.value)
        : [row.subcategory?._id || row.subcategory?.value].filter(Boolean)

      console.log('Row subcategories:', rowSubcategories)
      const subcategoryMatch =
        selectedSubcategories.length === 0 ||
        rowSubcategories.some((subId) => selectedSubcategories.includes(subId))
      console.log('subcategoryMatch: ', subcategoryMatch)

      return categoryMatch && subcategoryMatch
    })
  }, [data, selectedCategories, selectedSubcategories, unlimitedFaq])

  const tableInstance = useTable(
    { columns, data: filteredData, initialState: { pageIndex: 0 } },
    useGlobalFilter,
    useSortBy,
    usePagination
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    pageOptions,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    state: { pageIndex, pageSize, globalFilter },
  } = tableInstance

  useEffect(() => setPageSize(defaultValue || 10), [defaultValue])
  const setEntriesPerPage = (value) => setPageSize(value)

  const renderPagination = pageOptions.map((option) => (
    <MDPagination
      item
      key={option}
      onClick={() => gotoPage(Number(option))}
      active={pageIndex === option}
    >
      {option + 1}
    </MDPagination>
  ))

  const handleInputPagination = ({ target: { value } }) =>
    value > pageOptions.length || value < 0 ? gotoPage(0) : gotoPage(Number(value))
  const customizedPageOptions = pageOptions.map((option) => option + 1)
  const handleInputPaginationValue = ({ target: value }) => gotoPage(Number(value.value - 1))

  const [search, setSearch] = useState(globalFilter)
  const [filter, setFilter] = useState(globalFilter)
  const onSearchChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined)
  }, 100)

  const setSortedValue = (column) => {
    let sortedValue
    if (isSorted && column.isSorted) {
      sortedValue = column.isSortedDesc ? 'desc' : 'asce'
    } else if (isSorted) {
      sortedValue = 'none'
    } else {
      sortedValue = false
    }
    return sortedValue
  }

  const entriesStart = pageIndex === 0 ? pageIndex + 1 : pageIndex * pageSize + 1
  let entriesEnd
  if (pageIndex === 0) {
    entriesEnd = pageSize
  } else if (pageIndex === pageOptions.length - 1) {
    entriesEnd = rows.length
  } else {
    entriesEnd = pageSize * (pageIndex + 1)
  }
const handleBulkEditSuccess = () => {
  setSelectedFaqs([]);
  handleRefresh(); // Your existing refresh function
};
  return (
    <TableContainer sx={{ boxShadow: 'none' }}>
      {(entriesPerPage || canSearch || unlimitedFaq) && (
        <MDBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
          {entriesPerPage && (
            <MDBox display="flex" alignItems="center">
              <Autocomplete
                disableClearable
                value={pageSize.toString()}
                options={entries}
                onChange={(event, newValue) => {
                  setEntriesPerPage(parseInt(newValue, 10))
                }}
                size="small"
                sx={{ width: '5rem' }}
                renderInput={(params) => <MDInput {...params} />}
              />
              <MDTypography variant="caption" color="secondary">
                &nbsp;&nbsp;entries per page
              </MDTypography>
            </MDBox>
          )}

          {unlimitedFaq && (
            <>
              <MDBox ml={1}>
                <Multiselect
                  data={categories.map((cat) => ({
                    value: cat._id,
                    label: cat.name,
                  }))}
                  edit_data={[]}
                  isMulti={true}
                  name="category"
                  placeholder="category"
                  onChange={handleCategoryChange}
                />
              </MDBox>

              <MDBox ml={1}>
                <Multiselect
                  data={subcategories.map((sub) => ({
                    value: sub._id,
                    label: sub.name,
                  }))}
                  edit_data={[]}
                  isMulti={true}
                  name="subcategory"
                  placeholder="subcategory"
                  onChange={handleSubcategoryChange}
                />
              </MDBox>
            </>
          )}

          {canFilter && (
            <>
              {typeOptions && (
                <MDBox width="12rem" ml="10px">
                  <Autocomplete
                    options={typeOptions || ['user', 'agency', 'location', 'LevelUpMarketplace']}
                    value={filter || ''}
                    onChange={(event, newValue) => {
                      setFilter(newValue)
                      onSearchChange(newValue || '')
                    }}
                    renderInput={(params) => (
                      <MDInput {...params} placeholder="Select filter..." size="small" fullWidth />
                    )}
                    disableClearable={false}
                    size="small"
                  />
                </MDBox>
              )}
              <MDBox width="12rem" ml="10px">
                <Autocomplete
                  options={['Draft', 'Publish']}
                  value={filter || ''}
                  onChange={(event, newValue) => {
                    setFilter(newValue)
                    onSearchChange(newValue || '')
                  }}
                  renderInput={(params) => (
                    <MDInput {...params} placeholder="Select filter..." size="small" fullWidth />
                  )}
                  disableClearable={false}
                  size="small"
                />
              </MDBox>
            </>
          )}

          {canSearch && (
            <MDBox display="flex" alignItems="center" gap={2} width="18rem" ml="5px" fullWidth>
              {unlimitedFaq && selectedFaqs.length > 0 && (
                <BulkFaqEdit
                  categries={categories}
                  handleRefresh={handleBulkEditSuccess}
                  editData={selectedFaqs}
                  subcategories={subcategories}
                />
              )}
              <MDInput
                ml="1px"
                placeholder="Search..."
                value={search}
                size="small"
                fullWidth
                onChange={({ currentTarget }) => {
                  setSearch(currentTarget.value)
                  onSearchChange(currentTarget.value)
                }}
              />
            </MDBox>
          )}
        </MDBox>
      )}

      <Table {...getTableProps()}>
        <MDBox component="thead">
          {headerGroups.map((headerGroup) => (
            <TableRow {...headerGroup.getHeaderGroupProps()}>
              {unlimitedFaq && (
                <DataTableHeadCell width="4px" align="left">
                  Change Category
                </DataTableHeadCell>
              )}
              {headerGroup.headers?.map((column) => (
                <DataTableHeadCell
                  {...column.getHeaderProps(isSorted && column.getSortByToggleProps())}
                  width={column.width ? column.width : 'auto'}
                  align={column.align ? column.align : 'left'}
                  sorted={setSortedValue(column)}
                  key={column.id}
                >
                  {column.render('Header')}
                </DataTableHeadCell>
              ))}
            </TableRow>
          ))}
        </MDBox>
        <TableBody {...getTableBodyProps()}>
          {page.map((row, key) => {
            prepareRow(row)
            return (
              <TableRow {...row.getRowProps()} key={row.id}>
              {unlimitedFaq && (
                  <DataTableBodyCell align="left">
                    <input
                      type="checkbox"
                      onChange={(e) => handleCheckboxChange(e, row)}
                      checked={selectedFaqs.includes(row.original)}
                    />
                  </DataTableBodyCell>
                )}
                {row.cells?.map((cell) => (
                  <DataTableBodyCell
                    noBorder={noEndBorder && rows.length - 1 === key}
                    align={cell.column.align ? cell.column.align : 'left'}
                    {...cell.getCellProps()}
                    key={cell.id}
                  >
                    {cell.render('Cell')}
                  </DataTableBodyCell>
                ))}
              </TableRow>
            )
          })}
        </TableBody>
      </Table>

      <MDBox
        display="flex"
        flexDirection={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        p={!showTotalEntries && pageOptions.length === 1 ? 0 : 3}
      >
        {showTotalEntries && (
          <MDBox mb={{ xs: 3, sm: 0 }}>
            <MDTypography variant="button" color="secondary" fontWeight="regular">
              Showing {entriesStart} to {entriesEnd} of {rows.length} entries
            </MDTypography>
          </MDBox>
        )}
        {pageOptions.length > 1 && (
          <MDPagination
            variant={pagination.variant ? pagination.variant : 'gradient'}
            color={pagination.color ? pagination.color : 'info'}
          >
            {canPreviousPage && (
              <MDPagination item onClick={() => previousPage()}>
                <Icon sx={{ fontWeight: 'bold' }}>chevron_left</Icon>
              </MDPagination>
            )}
            {renderPagination.length > 6 ? (
              <MDBox width="5rem" mx={1}>
                <MDInput
                  inputProps={{ type: 'number', min: 1, max: customizedPageOptions.length }}
                  value={customizedPageOptions[pageIndex]}
                  onChange={(handleInputPagination, handleInputPaginationValue)}
                />
              </MDBox>
            ) : (
              renderPagination
            )}
            {canNextPage && (
              <MDPagination item onClick={() => nextPage()}>
                <Icon sx={{ fontWeight: 'bold' }}>chevron_right</Icon>
              </MDPagination>
            )}
          </MDPagination>
        )}
      </MDBox>
    </TableContainer>
  )
}

DataTable.defaultProps = {
  entriesPerPage: { defaultValue: 10, entries: [5, 10, 15, 20, 25] },
  canSearch: false,
  canFilter: false,
  showTotalEntries: true,
  pagination: { variant: 'gradient', color: 'info' },
  isSorted: true,
  noEndBorder: false,
  categories: [],
  subcategories: [],
}

DataTable.propTypes = {
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  canSearch: PropTypes.bool,
  canFilter: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(['contained', 'gradient']),
    color: PropTypes.oneOf([
      'primary',
      'secondary',
      'info',
      'success',
      'warning',
      'error',
      'dark',
      'light',
    ]),
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
  unlimitedFaq: PropTypes.bool,
  handleRefresh: PropTypes.func,
  categories: PropTypes.arrayOf(PropTypes.object),
  subcategories: PropTypes.arrayOf(PropTypes.object),
}

export default DataTable
